import React, { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { ACTIVE, JobCard, useJobPoll } from "./ReimageJobBits";

const TARGETS = { stock: "Stock photo (Pexels) → branded card fallback", card: "Branded headline card only ($0, no Pexels)" };
const SELECT = "admin-input bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm max-w-md w-full";

function Line({ n, children, warn }) {
  const tone = n ? (warn ? "text-amber-300" : "text-zinc-200") : "text-zinc-600";
  return <li className={tone}><span className="mono inline-block w-10 text-right mr-2 tabular-nums">{n}</span>{children}</li>;
}

function Plan({ plan, policy, busy, onRun, onCancel }) {
  const nothing = !plan.overrides.length && !plan.total && plan.current_default === policy;
  return (
    <div data-testid="compliance-plan" className="border border-zinc-800 rounded-sm p-3 space-y-2 text-xs">
      <div className="text-zinc-200">
        Network default: <span className="mono">{plan.current_default}</span>
        {plan.current_default !== policy && <> → <span className="mono text-amber-300">{policy}</span></>}
        {plan.current_default === policy && <span className="text-zinc-500"> (unchanged)</span>}
      </div>
      <ul className="space-y-1">
        <Line n={plan.overrides.length} warn>site override(s) reset to "Network default"</Line>
        <Line n={plan.source_sites} warn>site(s) currently resolving to the publisher's photos</Line>
        <Line n={plan.total}>article(s) still carrying a publisher photo or no image → re-imaged</Line>
      </ul>
      {plan.overrides.length > 0 && (
        <div data-testid="compliance-overrides" className="max-h-32 overflow-auto divide-y divide-zinc-900 border border-zinc-900 rounded-sm">
          {plan.overrides.map((o) => (
            <div key={o.site_id} data-testid={`compliance-override-${o.site_id}`} className="flex justify-between px-2 py-1 mono text-[11px]">
              <span className="text-zinc-300 truncate">{o.domain || o.name}</span><span className={o.image_policy === "source" ? "text-amber-400" : "text-zinc-500"}>{o.image_policy} → network</span>
            </div>
          ))}
        </div>
      )}
      {policy === "stock" && !plan.pexels_configured && (
        <div data-testid="compliance-no-pexels" className="text-amber-300">No Pexels key configured — every article gets a branded card. Paste a key above first, or pick "card" explicitly.</div>
      )}
      <div className="text-zinc-500 mono">Pexels quota this hour {plan.quota?.hour_used}/{plan.quota?.hourly_max} · month {plan.quota?.month_used}/{plan.quota?.monthly_max}. Already-compliant stock/card images are kept.</div>
      <div className="flex gap-2">
        <button type="button" data-testid="compliance-run-btn" disabled={busy || nothing} onClick={onRun} className="px-3 py-1.5 text-xs font-semibold bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-40 rounded-sm">
          {nothing ? "Already compliant" : "Make all sites compliant"}
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-xs border border-zinc-800 rounded-sm text-zinc-400 hover:text-zinc-200">Cancel</button>
      </div>
    </div>
  );
}

/** One click: network default → every site inherits it → legacy articles re-imaged. */
export default function MakeCompliantPanel({ cfg, onDone }) {
  const [policy, setPolicy] = useState(cfg?.default_policy || "stock");
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { job, track } = useJobPoll(onDone);
  const running = ACTIVE.includes(job?.status);

  useEffect(() => { if (cfg?.default_policy) setPolicy(cfg.default_policy); }, [cfg?.default_policy]);

  async function call(dryRun) {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/images/make-compliant", { policy, dry_run: dryRun });
      if (dryRun) { setResult(null); setPlan(r.data); return; }
      setPlan(null); setResult(r.data);
      if (r.data.job_id) track(r.data.job_id, r.data.total); else onDone && onDone();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }

  return (
    <div data-testid="compliance-panel" className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <select data-testid="compliance-policy" value={policy} onChange={(e) => { setPolicy(e.target.value); setPlan(null); }} disabled={busy || running} className={SELECT}>
          {Object.entries(TARGETS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <button
          type="button" data-testid="compliance-preview-btn" disabled={busy || running} onClick={() => call(true)}
          className="px-3 py-1.5 text-xs border border-emerald-700 text-emerald-300 rounded-sm hover:border-emerald-400 disabled:opacity-50 flex items-center gap-1.5 shrink-0"
        >{busy && !plan ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />} Preview compliance run…</button>
      </div>
      <div className="text-[11px] text-zinc-500 leading-relaxed">
        Sets the network default, resets every per-site override back to "Network default", then re-images every live article that still carries a publisher photo (or none). Existing stock/card images are untouched.
      </div>
      {plan && <Plan plan={plan} policy={policy} busy={busy} onRun={() => call(false)} onCancel={() => setPlan(null)} />}
      {result && (
        <div data-testid="compliance-result" className="text-xs text-emerald-300 border border-emerald-900/60 bg-emerald-950/30 px-3 py-2 rounded-sm mono">
          Network default set to {result.policy} · {result.overrides_cleared} override(s) reset · {result.total ? `${result.total} article(s) queued for re-imaging` : "no articles needed re-imaging"}
        </div>
      )}
      {job && <JobCard job={job} />}
      {err && <div data-testid="compliance-error" className="text-xs text-red-400 border border-red-900/60 bg-red-950/40 px-3 py-2 rounded-sm font-mono">{err}</div>}
    </div>
  );
}
